/**
 * 10-turn script for the ski insole replay simulator.
 *
 * Spec: PO-5.1 — 滑雪智能鞋垫逐弯复盘网站
 *
 * The simulator consumes this script to synthesize 3500 frames at 100Hz
 * (35000ms total). Each turn entry carries both the diagnostic targets
 * (type / score / problem tags) and the physical parameters the generator
 * uses to shape the synthetic sensor streams (gyro, edge angle, pressure).
 *
 * Timeline layout (500ms inter-turn gaps, last turn ends at 34500ms):
 *   0     - 1500   : initial straight section
 *   1500  - 4500   : Turn 1  (Left,  Edging,  3000ms)
 *   5000  - 7800   : Turn 2  (Right, Edging,  2800ms)
 *   8300  - 11500  : Turn 3  (Left,  Carving, 3200ms)
 *   12000 - 14500  : Turn 4  (Right, Skidding,2500ms)
 *   15000 - 17700  : Turn 5  (Left,  Edging,  2700ms)
 *   18200 - 21500  : Turn 6  (Right, Carving, 3300ms)
 *   22000 - 24600  : Turn 7  (Left,  Skidding,2600ms)
 *   25100 - 28000  : Turn 8  (Right, Edging,  2900ms)
 *   28500 - 30900  : Turn 9  (Left,  Skidding,2400ms)
 *   31400 - 34500  : Turn 10 (Right, Edging,  3100ms)
 *   34500 - 35000  : final straight section
 *
 * Direction convention for peak_gyro_z:
 *   positive = Left turn,  negative = Right turn.
 */

import { TurnDirection, TurnType, SportType } from '../types/enums';

/** A single turn entry inside the run script. */
export interface TurnScriptEntry {
  turn_index: number;          // 1-10
  direction: TurnDirection;    // Left or Right (alternating)
  target_type: TurnType;       // Skidding/Edging/Carving
  target_score: number;        // 0-100
  start_ms: number;            // turn start timestamp
  end_ms: number;              // turn end timestamp
  duration_ms: number;         // end - start
  problem_tags: string[];      // e.g. ['pressure-not-transferred', 'shallow-edging']

  // Physical parameters for the simulator
  peak_gyro_z: number;         // rad/s, signed (positive=left, negative=right)
  avg_edge_angle: number;      // degrees, always positive
  edge_change_rate: number;    // degrees per sample point
  outside_pressure_peak: number; // 0-1, peak outside foot pressure ratio
  pressure_transfer_time: number; // ms, time to transfer pressure at entry
  edging_sync_offset: number;  // ms, left-right board sync offset (good ~30, bad ~110)
  gyro_variance: number;       // (rad/s)^2, high for skidding
  turn_roundness: number;      // 0-100 target roundness score
  turn_symmetry: number;       // 0-100 target symmetry score
}

/** Top-level run script consumed by the simulator. */
export interface RunScript {
  duration_ms: number;         // 35000
  frame_count: number;         // 3500
  sample_rate: number;         // 100 Hz
  sport_type: SportType;
  turns: TurnScriptEntry[];
}

/** Total run duration in milliseconds. */
export const RUN_DURATION_MS = 35000 as const;

/** Number of frames the simulator must synthesize. */
export const RUN_FRAME_COUNT = 3500 as const;

/** Sample rate in Hz. */
export const RUN_SAMPLE_RATE = 100 as const;

/** Inter-turn gap in milliseconds. */
export const INTER_TURN_GAP_MS = 500 as const;

/** Initial straight-section length in milliseconds. */
export const INITIAL_STRAIGHT_MS = 1500 as const;

/**
 * The canonical 10-turn script. Left/Right alternate and good/bad turns
 * are interleaved for contrast in the replay review.
 */
export const RUN_SCRIPT: RunScript = {
  duration_ms: RUN_DURATION_MS,
  frame_count: RUN_FRAME_COUNT,
  sample_rate: RUN_SAMPLE_RATE,
  sport_type: SportType.OutdoorSki,
  turns: [
    {
      turn_index: 1,
      direction: TurnDirection.Left,
      target_type: TurnType.Edging,
      target_score: 78,
      start_ms: 1500,
      end_ms: 4500,
      duration_ms: 3000,
      problem_tags: [],
      peak_gyro_z: 0.85,
      avg_edge_angle: 28,
      edge_change_rate: 2.5,
      outside_pressure_peak: 0.78,
      pressure_transfer_time: 120,
      edging_sync_offset: 35,
      gyro_variance: 0.12,
      turn_roundness: 82,
      turn_symmetry: 85,
    },
    {
      turn_index: 2,
      direction: TurnDirection.Right,
      target_type: TurnType.Edging,
      target_score: 75,
      start_ms: 5000,
      end_ms: 7800,
      duration_ms: 2800,
      problem_tags: [],
      peak_gyro_z: -0.8,
      avg_edge_angle: 26,
      edge_change_rate: 2.8,
      outside_pressure_peak: 0.75,
      pressure_transfer_time: 140,
      edging_sync_offset: 40,
      gyro_variance: 0.14,
      turn_roundness: 80,
      turn_symmetry: 82,
    },
    {
      turn_index: 3,
      direction: TurnDirection.Left,
      target_type: TurnType.Carving,
      target_score: 88,
      start_ms: 8300,
      end_ms: 11500,
      duration_ms: 3200,
      problem_tags: [],
      peak_gyro_z: 0.95,
      avg_edge_angle: 32,
      edge_change_rate: 2.0,
      outside_pressure_peak: 0.82,
      pressure_transfer_time: 100,
      edging_sync_offset: 25,
      gyro_variance: 0.08,
      turn_roundness: 90,
      turn_symmetry: 88,
    },
    {
      turn_index: 4,
      direction: TurnDirection.Right,
      target_type: TurnType.Skidding,
      target_score: 45,
      start_ms: 12000,
      end_ms: 14500,
      duration_ms: 2500,
      problem_tags: ['pressure-not-transferred'],
      peak_gyro_z: -0.55,
      avg_edge_angle: 18,
      edge_change_rate: 4.5,
      outside_pressure_peak: 0.55,
      pressure_transfer_time: 280,
      edging_sync_offset: 95,
      gyro_variance: 0.42,
      turn_roundness: 50,
      turn_symmetry: 55,
    },
    {
      turn_index: 5,
      direction: TurnDirection.Left,
      target_type: TurnType.Edging,
      target_score: 72,
      start_ms: 15000,
      end_ms: 17700,
      duration_ms: 2700,
      problem_tags: ['shallow-edging'],
      peak_gyro_z: 0.75,
      avg_edge_angle: 16,
      edge_change_rate: 3.5,
      outside_pressure_peak: 0.7,
      pressure_transfer_time: 160,
      edging_sync_offset: 60,
      gyro_variance: 0.18,
      turn_roundness: 75,
      turn_symmetry: 70,
    },
    {
      turn_index: 6,
      direction: TurnDirection.Right,
      target_type: TurnType.Carving,
      target_score: 85,
      start_ms: 18200,
      end_ms: 21500,
      duration_ms: 3300,
      problem_tags: [],
      peak_gyro_z: -0.92,
      avg_edge_angle: 30,
      edge_change_rate: 2.2,
      outside_pressure_peak: 0.8,
      pressure_transfer_time: 110,
      edging_sync_offset: 28,
      gyro_variance: 0.09,
      turn_roundness: 88,
      turn_symmetry: 86,
    },
    {
      turn_index: 7,
      direction: TurnDirection.Left,
      target_type: TurnType.Skidding,
      target_score: 42,
      start_ms: 22000,
      end_ms: 24600,
      duration_ms: 2600,
      problem_tags: ['pressure-not-transferred', 'shallow-edging'],
      peak_gyro_z: 0.5,
      avg_edge_angle: 14,
      edge_change_rate: 5.0,
      outside_pressure_peak: 0.5,
      pressure_transfer_time: 320,
      edging_sync_offset: 115,
      gyro_variance: 0.48,
      turn_roundness: 45,
      turn_symmetry: 50,
    },
    {
      turn_index: 8,
      direction: TurnDirection.Right,
      target_type: TurnType.Edging,
      target_score: 68,
      start_ms: 25100,
      end_ms: 28000,
      duration_ms: 2900,
      problem_tags: ['shallow-edging', 'edging-async'],
      peak_gyro_z: -0.7,
      avg_edge_angle: 15,
      edge_change_rate: 4.0,
      outside_pressure_peak: 0.68,
      pressure_transfer_time: 180,
      edging_sync_offset: 110,
      gyro_variance: 0.22,
      turn_roundness: 72,
      turn_symmetry: 65,
    },
    {
      turn_index: 9,
      direction: TurnDirection.Left,
      target_type: TurnType.Skidding,
      target_score: 38,
      start_ms: 28500,
      end_ms: 30900,
      duration_ms: 2400,
      problem_tags: ['pressure-not-transferred', 'shallow-edging'],
      peak_gyro_z: 0.45,
      avg_edge_angle: 12,
      edge_change_rate: 5.5,
      outside_pressure_peak: 0.48,
      pressure_transfer_time: 350,
      edging_sync_offset: 120,
      gyro_variance: 0.52,
      turn_roundness: 40,
      turn_symmetry: 45,
    },
    {
      turn_index: 10,
      direction: TurnDirection.Right,
      target_type: TurnType.Edging,
      target_score: 76,
      start_ms: 31400,
      end_ms: 34500,
      duration_ms: 3100,
      problem_tags: [],
      peak_gyro_z: -0.78,
      avg_edge_angle: 27,
      edge_change_rate: 2.6,
      outside_pressure_peak: 0.76,
      pressure_transfer_time: 130,
      edging_sync_offset: 38,
      gyro_variance: 0.13,
      turn_roundness: 80,
      turn_symmetry: 83,
    },
  ],
};

/**
 * Returns the canonical 10-turn run script used by the simulator.
 */
export function getTurnScript(): RunScript {
  return RUN_SCRIPT;
}

/**
 * Returns a single turn entry by its 1-based index.
 * Returns `undefined` when the index is out of range.
 */
export function getTurnByIndex(index: number): TurnScriptEntry | undefined {
  if (!Number.isInteger(index) || index < 1 || index > RUN_SCRIPT.turns.length) {
    return undefined;
  }
  return RUN_SCRIPT.turns[index - 1];
}
